import {HttpGetUsersOptions, HttpInfiniteUsersResponse} from '@/features/users/http/users.http'
import {useGetInfiniteUsers} from '@/features/users/queries/useGetInfiniteUsers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {UseInfiniteQueryOptions} from '@tanstack/react-query'
import {z} from 'zod'
import {useGetUserGroups} from '@/features/user-groups/queries/useGetUserGroups'
import {UserSmall} from '@/features/users/types'
import {UserGroup} from '@/features/user-groups/types'
import {useMemo} from 'react'

export interface CombinedItem {
    type: 'user' | 'group' | 'section-header'
    id: string
    name: string
    fullName?: string
    email?: string
    avatarUrl?: string | null
    members?: {id: string; name: string; fullName: string}[]
}

export const useUsersAndGroupsSearchableList = (
    promiseArgs: Omit<HttpGetUsersOptions['params'], 'page' | 'search'>,
    options?: Pick<UseInfiniteQueryOptions<z.infer<typeof HttpInfiniteUsersResponse>>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const usersQuery = useGetInfiniteUsers(
        {
            ...promiseArgs,
            search: searchValue
        },
        options
    )

    const groupsQuery = useGetUserGroups(
        {
            search: searchValue
        },
        {enabled: options?.enabled}
    )

    const remappedData = useMemo(() => {
        const result: CombinedItem[] = []

        const users: CombinedItem[] = (
            usersQuery.data?.pages[0]?.data?.length ? usersQuery.data.pages.flatMap(page => page.data) : []
        ).map((user: UserSmall) => ({
            type: 'user' as const,
            id: user.id,
            name: user.fullName,
            fullName: user.fullName,
            email: user.email,
            avatarUrl: user.avatarUrl
        }))

        const groups: CombinedItem[] = (groupsQuery.data?.data || []).map((group: UserGroup) => ({
            type: 'group' as const,
            id: group.id,
            name: group.name,
            members: group.members.map(member => ({
                id: member.id,
                name: member.firstName,
                fullName: `${member.firstName} ${member.lastName}`
            }))
        }))

        // Add groups section if there are groups
        if (groups.length > 0) {
            result.push({
                type: 'section-header' as const,
                id: 'groups-header',
                name: 'Gruppi' // Italian for Groups
            })
            result.push(...groups)
        }

        // Add users section if there are users
        if (users.length > 0) {
            result.push({
                type: 'section-header' as const,
                id: 'users-header',
                name: 'Operatori Sanitari' // Italian for Healthcare Professionals
            })
            result.push(...users)
        }

        return result
    }, [usersQuery.data, groupsQuery.data])

    const isLoading = usersQuery.isLoading || groupsQuery.isLoading
    const isError = usersQuery.isError || groupsQuery.isError
    const hasNextPage = usersQuery.hasNextPage
    const fetchNextPage = usersQuery.fetchNextPage

    return {
        ...usersQuery,
        isLoading,
        isError,
        hasNextPage,
        fetchNextPage,
        remappedData,
        searchValue,
        onResetSearch,
        onSearch
    }
}
